import { ErrorHandler, Injectable } from '@angular/core'

import { LemonTranslationService } from '../../../_jaettu-angular/service/lemon-translation.service'
import { TimestampService } from '../../../_jaettu-angular/service/timestamp-service'

import { KayttajaService } from '../kayttaja.service'
import { PdfService } from '../pdf.service'

import { TositeUriService } from '@lemon/core-firebase/service/tosite/tosite-uri.service'
import { TositeIndeksoija } from '@lemon/core-firebase/service/tosite/tosite.indeksoija'
import { TositeKopioija } from '@lemon/core-firebase/service/tosite/tosite.kopioija'
import { annaTositteenHistoriakopio } from '@lemon/core-firebase/service/tosite/tositteen-historia'
import { TuettuKieli } from '@lemon/core/model/common'
import { CurrencyService } from '@lemon/core/service/currency.service'
import { DateService } from '@lemon/core/service/date.service'

import { KayttajanTiedot, Roolit } from '@lemon/core-firebase/model/kayttaja'
import { KuitinFirestoreTyojonoMerkinta } from '@lemon/core-firebase/model/kuitti-tyojonon-malli'
import { WebSelvityspyyntoTallennaPyynto, WebSelvityspyyntoTallennaVastaus } from '@lemon/core-firebase/model/web-tosite-api'
import {
  FirestoreTosite,
  FirestoreTositteenAlkuperainenTiedosto,
  FirestoreTositteenKuva,
  Maksutapa,
  MYYNTITOSITE_MAKSUTAPA,
  MYYNTITOSITE_MAKSUTAPA_ID,
  PALKKATOSITE_MAKSUTAPA,
  PALKKATOSITE_MAKSUTAPA_ID,
  RealtimeDbAvainToFirestoreAvainMappaus,
  ReplikoiTositeTyojonoData,
  SAHKOISET_LASKUT_MAKSUTAPA,
  TILIOTETOSITE_MAKSUTAPA,
  TILIOTETOSITE_MAKSUTAPA_ID,
  UUDEN_KUITIN_AVAIN,
} from '@lemon/core-firebase/model/tosite'

import { FieldTree } from '@angular/forms/signals'
import { LaskuToimittaja } from '@lemon/core-firebase/model/lasku'
import { IFirestoreBatch } from 'app/_jaettu-angular/base-firebase.service'
import { SimplifiedUploadData, TiedostojenLataamisService, UploadData } from 'app/_jaettu-angular/service/tiedostojen-lataamis.service'
import { TositeKuvaCacheService } from 'app/tositteet/kuvat/tosite-kuva-cache.service'
import { BehaviorSubject, combineLatest, firstValueFrom, Observable, of as observableOf } from 'rxjs'
import { first, map, mergeMap, switchMap } from 'rxjs/operators'
import { LaskuService } from '../../../_angular/service/lasku/lasku.service'
import { FileDropEntry } from '../../../_jaettu-angular/file-drop/file-drop-entry'
import { LemonBarcodeDetector, QrTaiViivakoodi } from '../barcode/barcode-detector.service'
import { FirebaseLemonaid } from '../firebase-lemonaid.service'
import { LemonHttpService } from '../lemon-http.service'

export interface PurettuTiedosto {
  type: 'img' | 'pdf'
  koodi: Promise<QrTaiViivakoodi>
  kasitellytKuvat: FirestoreTositteenKuva[]
  virhe: 'latausvirhe' | 'purkuvirhe'
}
export interface AlvPortaatJaoletus {
  portaat: number[]
  oletusProsentti: number
}
interface TallennuksenEstavatAsiat {
  tallennusEstetty: boolean
  lataaminenKesken: boolean
}

@Injectable()
export class TositeService {
  maksutavatObservable: Observable<Maksutapa[]>
  expandedMaksutavatObservable: Observable<Maksutapa[]>

  private previouslyUsedMaksutapaSubject = new BehaviorSubject<Maksutapa>(null)

  constructor(
    private errorHandler: ErrorHandler,
    private kayttajaService: KayttajaService,
    private lemonaidFirebase: FirebaseLemonaid,
    private tositeUriService: TositeUriService,
    private kuittiKopioija: TositeKopioija,
    private kuittiIndeksoija: TositeIndeksoija,
    private dateService: DateService,
    private timestampService: TimestampService,
    private currencyService: CurrencyService,
    private translationService: LemonTranslationService,
    private tiedostojenLataamisService: TiedostojenLataamisService,
    private pdfService: PdfService,
    private tositeKuvaCacheService: TositeKuvaCacheService,
    private httpService: LemonHttpService,
    private lemonBarcodeDetector: LemonBarcodeDetector,
    private laskuService: LaskuService,
  ) {
    this.maksutavatObservable = this.kayttajaService.kayttajaObservable.pipe(
      map(kayttaja => {
        const tulos: Maksutapa[] = []
        const sahkoinenMaksutapa: Maksutapa = {
          a: false,
          d: 0,
          i: SAHKOISET_LASKUT_MAKSUTAPA.i,
          img: SAHKOISET_LASKUT_MAKSUTAPA.img,
          l: SAHKOISET_LASKUT_MAKSUTAPA.l,
          o: 1,
          v: SAHKOISET_LASKUT_MAKSUTAPA.v,
        }
        tulos.push(sahkoinenMaksutapa)
        let o = 2
        for (const maksutapa of kayttaja?.tositteenMaksutavat || []) {
          const realtimeDbMaksutapa: Maksutapa = {
            a: maksutapa.aktiivinen,
            d: maksutapa.oletus ? 1 : 0,
            i: maksutapa.tunniste,
            img: maksutapa.base64Kuva,
            l: maksutapa.nimiListauksessa,
            o: o,
            v: maksutapa.nimiMuokkauksessa,
          }
          tulos.push(realtimeDbMaksutapa)
          o++
        }
        return tulos
      }),
    )

    this.expandedMaksutavatObservable = this.kayttajaService.kayttajaObservable.pipe(
      switchMap(kayttaja => {
        if (!kayttaja) {
          return observableOf<Maksutapa[]>([])
        }
        return this.maksutavatObservable.pipe(
          map(maksutavat => {
            const lisamaksutavat: Maksutapa[] = []
            if (kayttaja?.roolit[Roolit.TOSITTEET_MYYNTI]) {
              lisamaksutavat.push(MYYNTITOSITE_MAKSUTAPA)
            }
            if (kayttaja?.roolit[Roolit.TOSITTEET_TILI]) {
              lisamaksutavat.push(TILIOTETOSITE_MAKSUTAPA)
            }
            if (kayttaja?.roolit[Roolit.TOSITTEET_PALKKA]) {
              lisamaksutavat.push(PALKKATOSITE_MAKSUTAPA)
            }
            return maksutavat.concat(lisamaksutavat)
          }),
        )
      }),
    )
  }

  annaKuittiObservable(id: string): Observable<FirestoreTosite> {
    return this.kayttajaService.kayttajanTiedotObservable.pipe(
      mergeMap(kayttajanTiedot => {
        return this.annaKuittiObservableAsiakkaalle(kayttajanTiedot.asiakasAvain, id)
      }),
    )
  }

  annaKuittiObservableAsiakkaalle(asiakasAvain: string, id: string): Observable<FirestoreTosite> {
    const kuittiUri = this.tositeUriService.annaTositteenFirestoreUri(asiakasAvain, id)
    return this.lemonaidFirebase
      .firestoreDoc<FirestoreTosite>(kuittiUri)
      .listen()
      .pipe(
        map(kuitti => {
          if (kuitti) {
            delete kuitti['haku']
          }
          return kuitti
        }),
      )
  }

  annaUusiKuitti(maksutapa?: Maksutapa): FirestoreTosite {
    const nyt = this.timestampService.now()
    const localNyt = this.dateService.timestampToLocalDate(nyt)
    const kuitti: FirestoreTosite = {
      alvVahennysoikeus: null,
      avain: UUDEN_KUITIN_AVAIN,
      type: 'tosite',
      paivitetty: null,
      kuvakansio: this.lemonaidFirebase.databaseCreatePushId(),
      summa: null,
      sum: null,
      kayttajaUid: null,
      kuvat: {},
      lahde: null,
      maksutapa: maksutapa ? maksutapa.i : null,
      poistettu: false,
      pvm: nyt,
      localPvm: localNyt,
      p: this.dateService.localDateToNumber(localNyt),
      selite: null,
      tallennettuKannykassa: nyt,
      vastaanotettuPalvelimelle: nyt,
      alkuperaiset: {},
      paivitykset: {},
    }
    return kuitti
  }

  tallennaKuitit(kuitit: FirestoreTosite[]): Promise<void> {
    if (kuitit && kuitit.length > 0) {
      return this.kayttajaService.getKayttajanTiedot().then(kayttajanTiedot => {
        const batch = this.lemonaidFirebase.firestoreBatch()
        for (const kuitti of kuitit) {
          this.lisaaTallennettavaKuittiBatchiin(kuitti, kayttajanTiedot, batch)
        }
        return batch.commit()
      })
    }
    return Promise.resolve()
  }

  /**
   * Kuten sovelluksessa: selvityspyynnön kortista avatun tositteen muokkaus vastaa pyyntöön ja palauttaa sen kirjanpitäjälle.
   * Tosite on jo tallennettu, joten epäonnistunut vastaus vain kirjataan eikä estä paluuta listaan; pyyntö jää kortille avoimeksi.
   */
  async vastaaSelvityspyyntoonTositteenMuokkauksesta(selvityspyynnonAvain: string): Promise<void> {
    try {
      const vastaus = this.translationService.lokalisoi('tositteet.selvitys.tositetta-muokattu')
      const tulos = await this.lemonaidFirebase.functionsCall<WebSelvityspyyntoTallennaPyynto, WebSelvityspyyntoTallennaVastaus>(
        'tositeSelvityspyyntoTallennaWeb',
        { avain: selvityspyynnonAvain, vastaus },
        undefined,
        'europe-north1',
      )
      if (tulos.e) {
        console.error(`Selvityspyynnön ${selvityspyynnonAvain} vastaus tositteen muokkauksesta epäonnistui: ${tulos.e}`)
      }
    } catch (err) {
      this.errorHandler.handleError(err)
    }
  }

  tallennaKuitti(kuitti: FirestoreTosite, einvoiceApproved?: boolean): Promise<void> {
    if (kuitti) {
      return this.kayttajaService.getKayttajanTiedot().then(kayttajanTiedot => {
        const batch = this.lemonaidFirebase.firestoreBatch()
        this.lisaaTallennettavaKuittiBatchiin(kuitti, kayttajanTiedot, batch, einvoiceApproved)
        return batch.commit()
      })
    }
    return Promise.resolve()
  }

  private lisaaTallennettavaKuittiBatchiin(kuitti: FirestoreTosite, kayttajanTiedot: KayttajanTiedot, batch: IFirestoreBatch, einvoiceApproved?: boolean): void {
    const tyojonoId = this.lemonaidFirebase.firestoreCreateId()
    const uusi = kuitti.avain === UUDEN_KUITIN_AVAIN

    // Aseta avaimet
    kuitti.avain = kuitti.avain && kuitti.avain !== UUDEN_KUITIN_AVAIN ? kuitti.avain : this.lemonaidFirebase.firestoreCreateId()

    if (kuitti.maksutiedot) {
      kuitti.maksutiedot.identifier = kuitti.avain
    }

    // Aseta tallentaja
    kuitti.kayttajaUid = kayttajanTiedot.uid

    if (kuitti.x) {
      kuitti.l = true
    }

    delete kuitti.x // Remove selvitettava prop whenever a user saved the kuitti (even if no changes)
    delete kuitti.m // Remove accountant's comment whenever a user saved the kuitti (even if no changes)
    delete kuitti.w // Remove payee a user saved the kuitti (even if no changes)

    kuitti.lahde = 'web'
    kuitti.paivitetty = this.timestampService.now()

    const tallennettava = this.kuittiKopioija.kopioiTosite(kuitti)
    const historiaKopio = this.kuittiKopioija.kopioiTosite(kuitti)
    const avainMappaus: RealtimeDbAvainToFirestoreAvainMappaus = {
      firestoreKey: tallennettava.avain,
    }

    // Urit
    const kuitinUri = this.tositeUriService.annaTositteenFirestoreUri(kayttajanTiedot.asiakasAvain, tallennettava.avain)
    const kuitinArkistoUri = this.tositeUriService.annaTositteenHistoriaFirestoreUri(kayttajanTiedot.asiakasAvain, tallennettava.avain, tyojonoId)
    const avainMappauksenUri = this.tositeUriService.annaRealtimeDbToFirestoreAvainMappausUri(kayttajanTiedot.asiakasAvain, tallennettava.kuvakansio) // Kuvakansiossa on itse asiassa realtimedb avain johtuen, well, legacy
    const tyojonoUri = this.tositeUriService.annaTyojonoWeb1AloitusUri(kayttajanTiedot.asiakasId, tyojonoId)

    const tyoData: KuitinFirestoreTyojonoMerkinta = {
      created: this.timestampService.now(),
      tosite: historiaKopio,
      uri: kuitinUri,
    }

    if (einvoiceApproved) {
      tyoData.einvoiceApproved = true
    }

    // Luo hakutiedot
    const asany = tallennettava as any
    asany['haku'] = this.kuittiIndeksoija.luoHakutiedotKuitille(tallennettava)

    // Tallenna
    batch.set(kuitinUri, tallennettava)
    batch.set(kuitinArkistoUri, annaTositteenHistoriakopio(historiaKopio, kayttajanTiedot))
    if (uusi) {
      batch.set(avainMappauksenUri, avainMappaus)
    }
    batch.set(tyojonoUri, tyoData)

    // Replikoi tosite
    if (this.kuittiKopioija.replikoidaankoTosite(kuitti)) {
      const tositeReplikointiUri = this.tositeUriService.getTositteenReplikointiTyojonoUri(kayttajanTiedot.asiakasId, this.lemonaidFirebase.firestoreCreateId())
      const tyojono: ReplikoiTositeTyojonoData = {
        asiakasId: kayttajanTiedot.asiakasId,
        kuittiAvain: kuitti.avain,
        created: this.timestampService.now(),
      }
      batch.set(tositeReplikointiUri, tyojono)
    }
  }

  tallennaToimittaja(toimittaja: LaskuToimittaja) {
    return this.kayttajaService.getKayttajanTiedot().then(kayttajanTiedot => {
      const batch = this.lemonaidFirebase.firestoreBatch()
      this.laskuService.lisaaToimittajaBatchiin(kayttajanTiedot.asiakasId, kayttajanTiedot.asiakasAvain, kayttajanTiedot.uid, toimittaja, batch)
      return batch.commit()
    })
  }

  annaPdfTiedostonNimi(kuitti: FirestoreTosite, kieli: TuettuKieli, lahde: 'osto' | 'myynti' | 'tiliote' | 'palkka'): string {
    const nimi = this.translationService.lokalisoi('tositteet.' + lahde + '.katsele.lataa-tositteen-nimi', kieli)
    return this.annaPdfTiedostonNimiLadattaessa(nimi, true, kuitti, kieli)
  }

  annaPdfTiedostonNimiMyyntitositteelle(kuitti: FirestoreTosite, kieli: TuettuKieli): string {
    const nimi = this.translationService.lokalisoi('tositteet.myynti.katsele.lataa-tositteen-nimi', kieli)
    return this.annaPdfTiedostonNimiLadattaessa(nimi, true, kuitti, kieli)
  }

  annaPdfTiedostonNimiOstotositteelle(kuitti: FirestoreTosite, kieli: TuettuKieli): string {
    const nimi = this.translationService.lokalisoi('tositteet.osto.katsele.lataa-tositteen-nimi', kieli)
    return this.annaPdfTiedostonNimiLadattaessa(nimi, true, kuitti, kieli)
  }

  annaPdfTiedostonNimiPalkkatositteelle(kuitti: FirestoreTosite, kieli: TuettuKieli): string {
    const nimi = this.translationService.lokalisoi('tositteet.palkka.katsele.lataa-tositteen-nimi', kieli)
    return this.annaPdfTiedostonNimiLadattaessa(nimi, true, kuitti, kieli)
  }

  annaPdfTiedostonNimiTiliotetositteelle(kuitti: FirestoreTosite, kieli: TuettuKieli): string {
    const nimi = this.translationService.lokalisoi('tositteet.tiliote.katsele.lataa-tositteen-nimi', kieli)
    return this.annaPdfTiedostonNimiLadattaessa(nimi, true, kuitti, kieli)
  }

  annaPdfTiedostonNimiOstotositteenAlkuperaiselle(kuitti: FirestoreTosite, kieli: TuettuKieli): string {
    const nimi = this.translationService.lokalisoiKielella('lasku.tiedostonimen-alku-ladattaessa-numerolla', kieli, { saaja: kuitti.seller || '' })
    return this.annaPdfTiedostonNimiLadattaessa(nimi, true, kuitti, kieli)
  }

  private annaPdfTiedostonNimiLadattaessa(nimi: string, naytaSumma: boolean, kuitti: FirestoreTosite, kieli: TuettuKieli): string {
    if (kuitti && kuitti.localPvm) {
      nimi += ' - ' + this.dateService.muotoilePaikallinenPaiva(kuitti.localPvm, kieli)
    }

    if (kuitti && naytaSumma) {
      nimi += ' - ' + this.currencyService.formatoiRaha(kuitti.summa, 'EUR', kieli)
    }
    nimi += '.pdf'

    return nimi
  }

  getMaksutapaNameForRouter(id: number): 'osto' | 'myynti' | 'tiliote' | 'palkka' {
    if (id === MYYNTITOSITE_MAKSUTAPA_ID) {
      return 'myynti'
    }
    if (id === TILIOTETOSITE_MAKSUTAPA_ID) {
      return 'tiliote'
    }
    if (id === PALKKATOSITE_MAKSUTAPA_ID) {
      return 'palkka'
    }
    return 'osto'
  }

  public async setLastViewedKuitinMaksutapa(maksutapaId: number) {
    if (maksutapaId === null || maksutapaId === undefined) {
      this.previouslyUsedMaksutapaSubject.next(null)
      return
    }
    const maksutavat = await firstValueFrom(this.expandedMaksutavatObservable)
    const lastViewedMaksutapa = maksutavat.find(m => m.i === maksutapaId) ?? null
    this.previouslyUsedMaksutapaSubject.next(lastViewedMaksutapa)
  }
  public getLastViewedKuitinMaksutapa() {
    return this.previouslyUsedMaksutapaSubject.value
  }

  private async _yritaPdfnPurkamista(
    tosite: FirestoreTosite,
    file: File,
    alkuperainen: FirestoreTositteenAlkuperainenTiedosto,
    kayttajanTiedot: KayttajanTiedot,
    etsiViivakooditiedot: boolean,
    paluuarvo: PurettuTiedosto,
  ): Promise<boolean> {
    const etsiViivakoodiPdfstaJosPyydettyMaxPages = 2
    const lataaSivuja = etsiViivakooditiedot ? etsiViivakoodiPdfstaJosPyydettyMaxPages : 1
    const fileAsUint8Array = await this.tiedostojenLataamisService.getAsUint8Array(file)
    const openData = await this.pdfService.isPdfOpenable(fileAsUint8Array, 720, lataaSivuja) // 2/3 1080p
    if (!openData?.success) {
      return false
    }

    paluuarvo.type = 'pdf'
    const sivutResult = this.tositeKuvaCacheService.hoidaPdfnRajayttaminenKuviksi(
      tosite,
      alkuperainen,
      kayttajanTiedot,
      openData,
      etsiViivakooditiedot ? etsiViivakoodiPdfstaJosPyydettyMaxPages : 0,
      paluuarvo.koodi,
    )
    paluuarvo.kasitellytKuvat = sivutResult.kuvat
    if (etsiViivakooditiedot) {
      paluuarvo.koodi = sivutResult.koodi.then(result => {
        if (result.qr || result.viiva) {
          return result
        }
        return this.pdfService.etsiViivakoodiPdfsta(openData.fileAsUint8Array).then(code => {
          if (code) {
            result.viiva = code
          }
          return result
        })
      })
    }
    return true
  }

  public async puraYksiTiedostoBackendissa(
    tosite: FirestoreTosite,
    uploadData: SimplifiedUploadData,
    alkuperainen: FirestoreTositteenAlkuperainenTiedosto,
    kayttajanTiedot: KayttajanTiedot,
    etsiViivakooditiedot: boolean,
    koodiPromise: Promise<QrTaiViivakoodi>,
  ): Promise<PurettuTiedosto> {
    const paluuarvo: PurettuTiedosto = {
      type: null,
      kasitellytKuvat: [],
      koodi: koodiPromise,
      virhe: null,
    }

    // We have to wait for this upload to be complete before asking backend to digest it
    const uploadStatus = await firstValueFrom(uploadData.statusObservable.pipe(first(value => value && value.done)))
    if (uploadStatus.error) {
      paluuarvo.virhe = 'latausvirhe'
      return paluuarvo
    }

    try {
      const sivutResult = await this.tositeKuvaCacheService.puraTiedostoBakkarissa(
        uploadData.uploadUri,
        tosite,
        uploadData.filename,
        alkuperainen,
        kayttajanTiedot,
        koodiPromise,
        etsiViivakooditiedot ? 2 : 0,
      )
      if (sivutResult.virhe) {
        paluuarvo.virhe = 'purkuvirhe'
        return paluuarvo
      }

      paluuarvo.kasitellytKuvat = sivutResult.kuvat
      paluuarvo.type = sivutResult.fileType

      return paluuarvo
    } catch (err) {
      console.error(err)
      paluuarvo.virhe = 'purkuvirhe'
      return paluuarvo
    }
  }

  async puraYksiTiedostoLokaalisti(
    tosite: FirestoreTosite,
    file: File,
    tiedosto: FileDropEntry,
    fileEnding: string,
    uploadData: SimplifiedUploadData,
    alkuperainen: FirestoreTositteenAlkuperainenTiedosto,
    kayttajanTiedot: KayttajanTiedot,
    etsiViivakooditiedot: boolean,
    koodiPromise: Promise<QrTaiViivakoodi>,
  ): Promise<PurettuTiedosto> {
    const paluuarvo: PurettuTiedosto = {
      type: null,
      kasitellytKuvat: [],
      koodi: koodiPromise,
      virhe: null,
    }

    // Pura kuviksi
    if (fileEnding.toLowerCase() === 'pdf') {
      const tulos = await this._yritaPdfnPurkamista(tosite, file, alkuperainen, kayttajanTiedot, etsiViivakooditiedot, paluuarvo)
      if (!tulos) {
        paluuarvo.virhe = 'purkuvirhe' // this.translationService.lokalisoi('kuitit.muokkaa.validation.pdf-parse-error', { tiedostonNimi: tiedosto.relativePath })
      }
    } else if (fileEnding.toLowerCase() === 'heif' || fileEnding.toLowerCase() === 'heic') {
      const sivuResult = this.tositeKuvaCacheService.hoidaHeifConvert(file, tosite, alkuperainen, kayttajanTiedot, etsiViivakooditiedot, paluuarvo.koodi)
      paluuarvo.kasitellytKuvat.push(sivuResult.kuva)
      paluuarvo.koodi = sivuResult.koodi
      paluuarvo.type = 'img'
    } else {
      const kuva: FirestoreTositteenKuva = {
        avain: alkuperainen.avain + '_1',
        jarjestys: 1,
        poistettu: false,
        type: 'jpg',
        alkuperaisenAvain: alkuperainen.avain,
      }

      if (etsiViivakooditiedot) {
        paluuarvo.koodi = paluuarvo.koodi.then(result => {
          if (result.qr || result.viiva) {
            return result
          }
          return this.lemonBarcodeDetector.detect(file).then(koodi => {
            // console.log('got koodi', koodi)
            if (koodi?.qr) {
              result.qr = koodi.qr
            } else if (koodi?.viiva) {
              result.viiva = koodi.viiva
            }
            return result
          })
        })
      }

      this.tositeKuvaCacheService.lisaaLisattyTiedostoCacheen(kayttajanTiedot, tosite, kuva, file)

      paluuarvo.kasitellytKuvat.push(kuva)
      paluuarvo.type = 'img'
    }
    return paluuarvo
  }

  async saveIsPreventedSimplified<T>(
    form: { valid: boolean } | FieldTree<T>,
    latausvirheetObservable: Observable<string[]>,
    uploadTasks: SimplifiedUploadData[],
    virheviestiPysyva?: string,
  ): Promise<TallennuksenEstavatAsiat> {
    if (!form || !latausvirheetObservable || !uploadTasks) {
      return {
        lataaminenKesken: false,
        tallennusEstetty: true,
      }
    }

    if (virheviestiPysyva) {
      return {
        lataaminenKesken: false,
        tallennusEstetty: true,
      }
    }

    const latausvirhe = await firstValueFrom(latausvirheetObservable).then(virheet => {
      return virheet && virheet.length > 0
    })
    if (latausvirhe) {
      return {
        lataaminenKesken: false,
        tallennusEstetty: true,
      }
    }

    const done =
      uploadTasks.length < 1
        ? true
        : await firstValueFrom(
            combineLatest(uploadTasks.map(t => t.statusObservable)).pipe(
              map(statuses => {
                for (const status of statuses) {
                  if (!status.done) {
                    return false
                  }
                }
                return true
              }),
            ),
          )

    if (!done) {
      return {
        tallennusEstetty: true,
        lataaminenKesken: true,
      }
    }

    const error =
      uploadTasks.length < 1
        ? false
        : await firstValueFrom(
            combineLatest(uploadTasks.map(t => t.statusObservable)).pipe(
              map(statuses => {
                for (const status of statuses) {
                  if (status.error) {
                    return true
                  }
                }
                return false
              }),
            ),
          )

    if (error) {
      return {
        tallennusEstetty: true,
        lataaminenKesken: false,
      }
    }

    return {
      lataaminenKesken: false,
      tallennusEstetty: !this._onkoLomakeKelvollinen(form),
    }
  }

  async saveIsPrevented<T>(
    form: { valid: boolean } | FieldTree<T>,
    virheviestiPysyva: string,
    latausvirheetObservable: Observable<string[]>,
    uploadTasks: UploadData[],
  ): Promise<TallennuksenEstavatAsiat> {
    if (!form || !latausvirheetObservable || !uploadTasks) {
      return {
        lataaminenKesken: false,
        tallennusEstetty: true,
      }
    }

    const paluuarvo: TallennuksenEstavatAsiat = {
      lataaminenKesken: false,
      tallennusEstetty: false,
    }

    if (virheviestiPysyva) {
      paluuarvo.tallennusEstetty = true
      return paluuarvo
    }

    const latausvirhe = await firstValueFrom(latausvirheetObservable).then(virheet => {
      return virheet && virheet.length > 0
    })
    if (latausvirhe) {
      return {
        lataaminenKesken: false,
        tallennusEstetty: true,
      }
    }

    for (const uploadData of uploadTasks) {
      if (!uploadData.done) {
        paluuarvo.tallennusEstetty = true
        paluuarvo.lataaminenKesken = true
        return paluuarvo
      }
    }

    return {
      lataaminenKesken: false,
      tallennusEstetty: !this._onkoLomakeKelvollinen(form),
    }
  }

  private _onkoLomakeKelvollinen<T>(lomake: { valid: boolean } | FieldTree<T>): boolean {
    return typeof lomake === 'function' ? lomake().valid() : lomake.valid
  }
}
